<template>
  <div>
    <h3 class="mb-3">Редактировать адрес</h3>
    <form @submit.prevent="updateLocation" @keydown.enter.prevent>
      <yandex-map
        :coords="yandexMapCoords"
        :zoom="15"
        :controls="['zoomControl', 'geolocationControl']"
        @click="onYandexMapClick"
        style="
          width: 600px;
          height: 400px;
          border: 1px solid #ddd;
          border-radius: 4px;
        "
      >
        <ymap-marker
          v-if="markerCoords"
          :coords="markerCoords"
          marker-id="yandex-map-marker"
        />
      </yandex-map>
      <div v-if="yandexMapAddress" class="mt-2">
        <p><strong>Выбранный адрес:</strong> {{ yandexMapAddress }}</p>
      </div>
      <div class="mb-3">
        <label>Поиск на карте</label>
        <va-input
          v-model="yandexMapSearchQuery"
          placeholder="Введите адрес для поиска"
          class="mb-2"
          @keyup.enter="searchYandexMapAddress"
        >
          <template #appendInner>
            <va-button
              size="small"
              color="primary"
              @click="searchYandexMapAddress"
              :disabled="!yandexMapSearchQuery"
            >
              Найти
            </va-button>
          </template>
        </va-input>
      </div>
      <div class="mb-3">
        <label for="address">Адрес</label>
        <va-input
          id="address"
          v-model="form.address"
          placeholder="Введите адрес"
        />
      </div>
      <div class="mb-3 mt-3">
        <label for="phone">Телефон</label>
        <va-input
          id="phone"
          v-model="form.phone"
          v-mask="'+7 (###) ###-##-##'"
          placeholder="+7 (999) 123-45-67"
        />
      </div>
      <div class="mb-3">
        <label for="schedule">Расписание</label>
        <va-input
          id="schedule"
          v-model="form.schedule"
          placeholder="Пн–Пт 09:00–20:00, Сб–Вс 10:00–18:00"
        />
      </div>
      <div class="mb-3">
        <va-checkbox
          v-model="form.show_on_contacts"
          label="Показывать в контактах"
        />
      </div>
      <div class="mb-3">
        <va-checkbox
          v-model="form.show_on_pickup"
          label="Показывать для самовывоза"
        />
      </div>
      <div class="mb-3">
        <va-checkbox v-model="form.is_active" label="Активен (публичный)" />
      </div>
      <div class="mt-3">
        <va-button color="danger" class="mr-1" @click.prevent="$emit('hide')">
          Отменить
        </va-button>
        <va-button color="info" type="submit"> Сохранить </va-button>
      </div>
    </form>
  </div>
</template>

<script>
import api from "@/api/axios";
import { mapActions } from "vuex";
import { yandexMap, ymapMarker } from "vue-yandex-maps";

export default {
  name: "LocationUpdate",
  components: {
    yandexMap,
    ymapMarker,
  },
  props: {
    location: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      yandexMapCoords: [55.00262456968209, 73.386825],
      yandexMapAddress: "",
      yandexMapSearchQuery: "",
      markerCoords: null,
      form: {
        address: "",
        phone: "",
        schedule: "",
        lat: null,
        lng: null,
        show_on_contacts: true,
        show_on_pickup: true,
        is_active: true,
      },
    };
  },
  watch: {
    location: {
      immediate: true,
      handler(value) {
        if (!value || !value.id) {
          return;
        }

        this.form.address = value.address || value.full_address || "";
        this.form.phone = value.contacts?.phone || value.phone || "";
        this.form.schedule = value.schedule || "";
        this.form.show_on_contacts = Boolean(value.show_on_contacts);
        this.form.show_on_pickup = Boolean(value.show_on_pickup);
        this.form.is_active =
          value.is_active !== undefined ? Boolean(value.is_active) : true;
        this.form.lat =
          typeof value.lat === "number"
            ? value.lat
            : value.coordinates?.lat ?? null;
        this.form.lng =
          typeof value.lng === "number"
            ? value.lng
            : value.coordinates?.lng ?? null;

        if (this.form.lat !== null && this.form.lng !== null) {
          this.yandexMapCoords = [this.form.lat, this.form.lng];
          this.markerCoords = [this.form.lat, this.form.lng];
        } else {
          this.markerCoords = null;
        }

        this.yandexMapAddress = this.form.address;
        this.yandexMapSearchQuery = this.form.address;
      },
    },
  },
  methods: {
    ...mapActions({
      fetchLocations: "locations/getLocations",
    }),
    async updateLocation() {
      if (!this.location || !this.location.id) {
        return;
      }

      try {
        await api.put(`/admin/locations/${this.location.id}`, {
          address: this.form.address,
          lat: this.form.lat,
          lng: this.form.lng,
          phone: this.form.phone,
          schedule: this.form.schedule,
          show_on_contacts: this.form.show_on_contacts,
          show_on_pickup: this.form.show_on_pickup,
          is_active: this.form.is_active,
        });
        await this.fetchLocations();
        this.$emit("hide");
      } catch (error) {
        console.error("Ошибка при обновлении адреса:", error);
        alert("Не удалось обновить адрес. Попробуйте еще раз.");
      }
    },
    async searchYandexMapAddress() {
      if (!this.yandexMapSearchQuery) {
        return;
      }

      try {
        const apiKey = "8f61b621-e389-4848-af4a-60fa20c8eb9c";
        const response = await fetch(
          `https://geocode-maps.yandex.ru/1.x/?apikey=${apiKey}&geocode=${encodeURIComponent(
            this.yandexMapSearchQuery
          )}&format=json&results=1`
        );

        if (!response.ok) {
          throw new Error("Ошибка при запросе к геокодеру");
        }

        const data = await response.json();

        if (
          data.response &&
          data.response.GeoObjectCollection &&
          data.response.GeoObjectCollection.featureMember.length > 0
        ) {
          const geoObject =
            data.response.GeoObjectCollection.featureMember[0].GeoObject;
          const pos = geoObject.Point.pos.split(" ").map(Number);
          const coords = [pos[1], pos[0]];

          this.yandexMapCoords = coords;
          this.markerCoords = coords;
          this.form.lat = coords[0];
          this.form.lng = coords[1];
          this.yandexMapAddress =
            geoObject.metaDataProperty.GeocoderMetaData.text;
          this.form.address = this.yandexMapAddress;
          this.yandexMapSearchQuery = "";
        } else {
          alert("Адрес не найден");
        }
      } catch (error) {
        console.error("Ошибка поиска адреса:", error);
        alert("Ошибка при поиске адреса. Попробуйте еще раз.");
      }
    },
    async onYandexMapClick(e) {
      if (!window.ymaps) {
        return;
      }
      const coords = e.get("coords");
      this.yandexMapCoords = coords;
      this.markerCoords = coords;
      this.form.lat = coords[0];
      this.form.lng = coords[1];

      try {
        const apiKey = "8f61b621-e389-4848-af4a-60fa20c8eb9c";
        const [lat, lon] = coords;
        const response = await fetch(
          `https://geocode-maps.yandex.ru/1.x/?apikey=${apiKey}&geocode=${lon},${lat}&format=json&results=1`
        );

        if (!response.ok) {
          throw new Error("Ошибка при запросе к геокодеру");
        }

        const data = await response.json();

        if (
          data.response &&
          data.response.GeoObjectCollection &&
          data.response.GeoObjectCollection.featureMember.length > 0
        ) {
          const geoObject =
            data.response.GeoObjectCollection.featureMember[0].GeoObject;
          this.yandexMapAddress =
            geoObject.metaDataProperty.GeocoderMetaData.text;
          this.form.address = this.yandexMapAddress;
        } else {
          this.yandexMapAddress = "Адрес не определен";
        }
      } catch (error) {
        console.error("Ошибка обратного геокодирования:", error);
        try {
          const myGeocoder = window.ymaps.geocode(coords);
          myGeocoder.then((res) => {
            const firstGeoObject = res.geoObjects.get(0);
            if (firstGeoObject) {
              this.yandexMapAddress = firstGeoObject.properties.get("text");
              this.form.address = this.yandexMapAddress;
            }
          });
        } catch (fallbackError) {
          console.error("Ошибка fallback геокодирования:", fallbackError);
          this.yandexMapAddress = "Адрес не определен";
        }
      }
    },
  },
};
</script>
